import { useState, useMemo } from 'react';
import {
  Page,
  PageTitleWithButton,
  Pagination,
  Button as SMButton,
  Table
} from '../../../../components';
import { Theme, Box, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { Edit, Add } from '@mui/icons-material';
import { ManageToolsFilter } from './manage-tools-filters';
import {
  getToolCategoryOptions,
  getToolPhaseOptions,
  getTools
} from '../../../../fetch/possessions';
import { useQuery } from 'react-query';
import { IDropdownResponse, IResponse } from '../../../../models';
import { ITool } from '../../../../models/possessions';
import { AddPossessionModal } from '../add-possession-modal';
import { PossessionType } from '../manage-model-home-furniture';

export const ManageTools = () => {
  const classes = useStyles();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [nameSearch, setNameSearch] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    Description?: string;
    Phase?: string;
    Category?: string;
  } | null>({});

  const [selectedSort, setSelectedSort] = useState<string>('Code');
  const [sortDirection, setSortDirection] = useState<{
    Code?: 'Asc' | 'Desc';
    Description?: 'Asc' | 'Desc';
  }>({
    Code: 'Asc'
  });

  const [selectedPhase, setSelectedPhase] = useState<string>('');
  const { isLoading: isLoadingPhases, data: phases } = useQuery<IDropdownResponse[], Error>(
    ['phases'],
    async () => await getToolPhaseOptions()
  );

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const { isLoading: isLoadingCategories, data: categories } = useQuery<IDropdownResponse[], Error>(
    ['categories'],
    async () => await getToolCategoryOptions()
  );

  const {
    isLoading: isLoadingTools,
    data: tools,
    isRefetching: isRefetchingTools
  } = useQuery<IResponse<ITool[]>, Error>(
    ['getTools', page, perPage, filters, selectedSort, sortDirection[selectedSort]],
    async () =>
      await getTools({
        page: page + 1,
        perPage,
        description: nameSearch,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort],
        category: selectedCategory,
        phase: selectedPhase
      }),
    {
      onSuccess: tools => {
        setRecordCount(tools.totalRecordCount);
      }
    }
  );

  const toolsData = useMemo(() => tools?.records ?? [], [tools]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleEdit = (original: any) => {
    alert(original);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Code',
        accessor: 'code',
        isServerSorted: selectedSort === 'Code',
        isServerSortedDesc: sortDirection.Code === 'Desc',
        handleClickColumn: () => handleClickColumn('Code')
      },
      {
        Header: 'Description',
        accessor: 'description',
        isServerSorted: selectedSort === 'Description',
        isServerSortedDesc: sortDirection.Description === 'Desc',
        handleClickColumn: () => handleClickColumn('Description')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <SMButton
                id="edit-county"
                type="button"
                color="primary"
                startIcon={<Edit />}
                onClick={() => handleEdit(original)}
              >
                Edit
              </SMButton>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolsData]);
  return (
    <>
      <Page title="Manage Tools">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Tools'
          }}
          button={
            <Box display="flex">
              <Link className={classes.link} to={`/development/possessions/manage-tools`}>
                <Button
                  id="add-tool"
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                  className={classes.addToolButton}
                  onClick={() => setModalIsOpen(true)}
                >
                  Add Tool
                </Button>
              </Link>
            </Box>
          }
        />
        <ManageToolsFilter
          isLoading={isLoadingPhases || isLoadingCategories}
          handleNameSearch={(clearSearch?: boolean) => {
            setPage(0);
            setFilters({
              ...filters,
              Description: nameSearch
            });

            if (clearSearch) {
              setNameSearch('');
            }
          }}
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              setFilters({
                Description: '',
                Phase: '',
                Category: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                Description: nameSearch,
                Phase: selectedPhase,
                Category: selectedCategory
              });
            }
          }}
          setPage={setPage}
          setHasAppliedFilters={setHasAppliedFilters}
          searchName={nameSearch}
          setSearchName={setNameSearch}
          hasAppliedFilters={hasAppliedFilters}
          phases={phases ?? []}
          isLoadingPhases={isLoadingPhases}
          selectedPhase={selectedPhase}
          setSelectedPhase={setSelectedPhase}
          categories={categories ?? []}
          isLoadingCategories={isLoadingCategories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
        <Table
          columns={columns}
          data={toolsData}
          isLoading={isLoadingTools || isRefetchingTools}
          hidePagination
        />
        {!(isLoadingTools || isRefetchingTools) && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Page>
      <AddPossessionModal
        possessionType={PossessionType.Tools}
        open={modalIsOpen}
        modalIsLoading={false}
        onClose={() => setModalIsOpen(false)}
      />
    </>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    textAlign: 'right'
  },
  link: {
    textDecoration: 'none'
  },
  addToolButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  }
}));
